/* WHERE A NUMBER CAME FROM — Max, 2026-09-22: "những chỗ tính toán tôi cần bạn cho tooltips là chữ
   i để user click vào thì sẽ ra công thức và cách tính để ra con số đó á".

   A CLICK, not a hover. Half the people reading this screen are on a tablet, where there is no
   hover at all; and a formula is something you read twice and compare against a vendor's sheet,
   which you cannot do while holding a pointer still. It is a real <button>, so it is reachable by
   keyboard and says whether it is open.

   THREE PARTS, always in the same order: the RULE in words, the same rule WITH THIS QUOTE'S
   NUMBERS in it, and the answer. The middle one is the reason this exists — a rule alone is a
   thing to be trusted, a substitution is a thing that can be checked against the sheet on the desk.

   It closes on Escape and on a click anywhere else, because a panel that can only be closed by
   finding its own button again is a panel people leave open and then read the wrong one. */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function FormulaHint({ title, rule, work, result, align = 'left' }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [shift, setShift] = useState(0);
  const wrapRef = useRef(null);
  const popRef = useRef(null);

  /* IT CLAMPS ITSELF — Max, 2026-09-23, on a screenshot of the MOQ surcharge hint hanging off the
     left of the card with half its words cut away. `align` is a static choice and cannot be right:
     the input grid changes its column count with the window, so the same field sits in the left
     column at one width and the right column at another, and `right: 0` on a left-column icon puts
     a 340px panel 340px to the LEFT of it.

     So the panel is measured once it is up and slid back inside the window — the shift is reset to
     zero before measuring, or the second open would measure a position this code had already
     moved. GUTTER is the same 12px the page keeps at its own edge. */

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    // Capture, so a click that another handler stops still closes this.
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) { setShift(0); return undefined; }
    const GUTTER = 12;
    const PAD = 4;
    const place = () => {
      const el = popRef.current;
      if (!el) return;
      el.style.transform = 'none';
      const r = el.getBoundingClientRect();
      /* THE CARD, not just the window. Clamping to the window alone still left the panel hanging
         102px over the sidebar at 1900px, because the card's own left edge is far from the
         window's. The two blocks this hint appears in are the input cards and the tables. */
      const box = el.closest('.sp-section, table');
      const b = box ? box.getBoundingClientRect() : null;
      const lo = Math.max(GUTTER, b ? b.left + PAD : GUTTER);
      const hi = Math.min(window.innerWidth - GUTTER, b ? b.right - PAD : window.innerWidth - GUTTER);
      const over = Math.min(0, hi - r.right) || 0;
      const under = Math.max(0, lo - r.left) || 0;
      // A panel wider than its box would otherwise be pushed by both, so the left edge wins: a
      // formula read from its first character is worth more than one read from its last.
      el.style.transform = '';
      setShift(under || over);
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  return (
    <span className="cp-fx-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`cp-fx${open ? ' on' : ''}`}
        aria-expanded={open}
        aria-label={t('pricing.fxOpen', { what: title })}
        /* The label is often a <label> wrapping an input, and a click inside one focuses that
           input — which would scroll the page out from under the panel being opened. */
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }}
      >
        i
      </button>
      {open && (
        <span
          className={`cp-fx-pop ${align}`}
          ref={popRef}
          role="dialog"
          aria-label={title}
          style={shift ? { transform: `translateX(${shift}px)` } : undefined}
        >
          <b>{title}</b>
          {!!rule && <span className="cp-fx-rule">{rule}</span>}
          {(work || []).map((line) => <span className="cp-fx-line" key={line}>{line}</span>)}
          {!!result && <span className="cp-fx-res">= {result}</span>}
        </span>
      )}
    </span>
  );
}
